import { ExclamationCircleOutlined } from '@ant-design/icons';
import { Button, Flex, Splitter, Tabs, Tag, Tooltip, Typography } from 'antd';
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import { useSegmentSummary } from '../../api/CodeFrames';
import { useMultipleResponseOptions } from '../../api/Questions';
import { useQuestions, useSurvey } from '../../api/Surveys';
import BackButton from '../../components/BackButton';
import Loading from '../../components/Loading';
import {
  QCPhase,
  SegmentType,
  SurveyDashboardTabs,
  SurveyProcessingType,
  SurveyStatus,
  UserRoleType,
} from '../../constants';
import useParams from '../../hooks/useParams';
import useRoles from '../../hooks/useRoles';
import ChatTab from './ChatTab';
import ResponsesTab from './ResponsesTab';
import SegmentsSummaryPanel from './SegmentsSummaryPanel';
import SentimentsTab from './SentimentsTab';
import SummaryTab from './SummaryTab';
import ThemesTab from './ThemesTab';

const { Panel } = Splitter;
const { Title } = Typography;

const StyledSplitter = styled(Splitter)`
  && .ant-splitter-bar-collapse-bar {
    opacity: unset;
    font-size: 14px;
    width: 14px;
    height: 40px;
  }

  && .ant-splitter-bar-dragger:after {
    height: 40px;
  }
`;

const StyledDiv = styled.div`
  padding: 0px 24px 24px;
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow: ${(props) => (props.$tab === SurveyDashboardTabs.CHAT ? 'hidden' : 'auto')};
`;

const StyledTitle = styled(Title)`
  margin-top: 16px;
`;

const StyledTabs = styled(Tabs)`
  height: 100%;
  .ant-tabs-content-holder {
    display: flex;
    height: 100%;
  }
  .ant-tabs-content {
    height: 100%;
  }
  .ant-tabs-tabpane {
    height: 100%;
  }
`;

function SurveyDashboard() {
  const navigate = useNavigate();
  const { surveyId } = useParams();
  const { roles } = useRoles();
  const [searchParams] = useSearchParams();
  const hasInitialized = useRef(false);

  const [activeTab, setActiveTab] = useState(SurveyDashboardTabs.SUMMARY);
  const [selectedQuestionId, setSelectedQuestionId] = useState(undefined);
  const [selectedSegmentQuestionId, setSelectedSegmentQuestionId] = useState(undefined);
  const [statAssistEnabled, setStatAssistEnabled] = useState(true);
  const [activeSegmentTypes, setActiveSegmentTypes] = useState([]);
  const [refetchInterval, setRefetchInterval] = useState(false);

  const {
    data: questions,
    isLoading: questionsLoading,
    isError: questionsError,
    refetch: refetchQuestions,
  } = useQuestions({ surveyId });

  const {
    data: survey,
    isLoading: surveyLoading,
    isError: surveyError,
  } = useSurvey(
    { surveyId },
    {
      refetchInterval,
      onSuccess: (data) => {
        if (!refetchInterval && data.data.survey_status === SurveyStatus.PUBLISH_IN_PROGRESS) {
          setRefetchInterval(5000);
        } else if (
          refetchInterval &&
          data.data.survey_status !== SurveyStatus.PUBLISH_IN_PROGRESS
        ) {
          refetchQuestions();
          setRefetchInterval(false);
        }
      },
    },
  );

  useEffect(() => {
    if (!questionsLoading && questions?.data && !hasInitialized.current) {
      hasInitialized.current = true;

      const questionId = parseInt(searchParams.get('questionId'), 10);
      if (questions.data.some((q) => q.has_themes && q.id === questionId)) {
        setSelectedQuestionId(questionId);
        setActiveTab(SurveyDashboardTabs.THEMES);
      } else {
        setSelectedQuestionId(questions.data.find((q) => q.has_themes)?.id);
      }
    }
  }, [questionsLoading, questions, searchParams]);

  const questionResponseOptions = useMultipleResponseOptions(
    questions?.data
      .filter((q) => q.is_filterable)
      .map((q) => ({
        questionId: q.id,
        surveyId,
        select: (data) => ({ ...data, questionId: q.id }),
      })),
  );

  const { data: segmentSummary, isLoading: segmentSummaryLoading } = useSegmentSummary(
    {
      codeFrameId: questions?.data.find((q) => q.id === selectedQuestionId)?.code_frame_id,
      segmentId: selectedSegmentQuestionId,
      surveyId,
    },
    {
      enabled: !!(selectedQuestionId && selectedSegmentQuestionId),
    },
  );

  const showSegmentSummary =
    activeTab === SurveyDashboardTabs.THEMES &&
    selectedSegmentQuestionId &&
    statAssistEnabled &&
    activeSegmentTypes.every((st) => st === SegmentType.ATTRIBUTE) &&
    questions?.data
      .find((q) => q.id === selectedQuestionId)
      ?.significant_segments.includes(selectedSegmentQuestionId);

  if (surveyLoading || questionsLoading) {
    return <Loading />;
  }

  if (surveyError || questionsError || !survey?.data || !questions?.data) {
    return (
      <StyledDiv>
        <BackButton onClick={() => navigate('/')} text="Back to My Projects" />
        <Typography.Text type="danger">
          We couldn&apos;t load the survey dashboard right now. Please try refreshing the page.
        </Typography.Text>
      </StyledDiv>
    );
  }

  const tabData = [
    {
      key: SurveyDashboardTabs.SUMMARY,
      label: 'Summary',
      children: (
        <SummaryTab
          survey={survey.data}
          questions={questions}
          setActiveTab={setActiveTab}
          setSelectedOpenEndedQuestionId={setSelectedQuestionId}
        />
      ),
    },
    {
      key: SurveyDashboardTabs.THEMES,
      label: 'Themes',
      children: (
        <ThemesTab
          survey={survey.data}
          questions={questions}
          selectedQuestionId={selectedQuestionId}
          setSelectedQuestionId={setSelectedQuestionId}
          questionResponseOptions={questionResponseOptions}
          selectedSegmentQuestionId={selectedSegmentQuestionId}
          setSelectedSegmentQuestionId={setSelectedSegmentQuestionId}
          statAssistEnabled={statAssistEnabled}
          setStatAssistEnabled={setStatAssistEnabled}
          highlightThemeIds={segmentSummary?.data?.theme_ids}
          setActiveSegmentTypes={setActiveSegmentTypes}
        />
      ),
    },
    {
      key: SurveyDashboardTabs.SENTIMENTS,
      label: 'Sentiments',
      children: (
        <SentimentsTab
          survey={survey.data}
          questions={questions}
          selectedQuestionId={selectedQuestionId}
          setSelectedQuestionId={setSelectedQuestionId}
        />
      ),
    },
    {
      key: SurveyDashboardTabs.RESPONSES,
      label: 'Responses',
      children: (
        <ResponsesTab
          survey={survey.data}
          questions={questions}
          selectedQuestionId={selectedQuestionId}
          setSelectedQuestionId={setSelectedQuestionId}
          questionResponseOptions={questionResponseOptions}
        />
      ),
    },
    {
      key: SurveyDashboardTabs.CHAT,
      label: (
        <>
          Fathomer{' '}
          <Tooltip title="Fathomer is our new research agent built to move from questions to clarity in minutes not hours.">
            <Tag color="blue" style={{ marginLeft: 4 }}>
              Beta
            </Tag>
          </Tooltip>
        </>
      ),
      children: <ChatTab survey={survey} />,
    },
  ];

  const showSurveyHubButton =
    (survey.data.processing_type === SurveyProcessingType.PRO ||
      survey.data.survey_status === SurveyStatus.PUBLISHED) &&
    (roles.includes(UserRoleType.ADMINISTRATOR) || roles.includes(UserRoleType.EDITOR));

  return (
    <StyledSplitter>
      <Panel defaultSize="70%" min="60%">
        <StyledDiv $tab={activeTab}>
          <BackButton onClick={() => navigate('/')} text="Back to My Projects" />
          <Flex gap={12} align="baseline">
            <StyledTitle level={4}>{survey.data.name}</StyledTitle>
            {questions.data.some((q) => q.has_themes && q.qc_phase !== QCPhase.COMPLETE) && (
              <Tag icon={<ExclamationCircleOutlined />} color="warning">
                <strong>Work in progress!</strong> Coding isn&apos;t complete, and results may still
                shift.
              </Tag>
            )}
            {survey.data.survey_status === SurveyStatus.PUBLISH_IN_PROGRESS && (
              <Tag icon={<ExclamationCircleOutlined />} color="warning">
                <strong>Analysis underway!</strong> StatAssist and reporting are in progress and
                will appear once ready.
              </Tag>
            )}
          </Flex>
          <StyledTabs
            activeKey={activeTab}
            onChange={setActiveTab}
            items={tabData}
            tabBarExtraContent={
              <Flex>
                {showSurveyHubButton && (
                  <Button onClick={() => navigate(`/surveys/${surveyId}/hub`)}>Project Hub</Button>
                )}
              </Flex>
            }
          />
        </StyledDiv>
      </Panel>
      {showSegmentSummary && (
        <Panel min="20%" collapsible>
          <SegmentsSummaryPanel
            question={questions?.data.find((q) => q.id === selectedQuestionId)}
            segmentQuestion={questions?.data.find((q) => q.id === selectedSegmentQuestionId)}
            summary={segmentSummary?.data}
            loading={segmentSummaryLoading}
            responseOptions={
              questionResponseOptions.find(
                (opt) => opt.data?.questionId === selectedSegmentQuestionId,
              )?.data.data
            }
          />
        </Panel>
      )}
    </StyledSplitter>
  );
}

export default SurveyDashboard;
